/**
 * UPI helpers (NPCI deep-link format). Mirrors backend/utils/upi.js.
 */

const VPA_RE = /^[a-zA-Z0-9][a-zA-Z0-9._-]{1,255}@[a-zA-Z][a-zA-Z0-9.-]{1,63}$/;
const TXN_RE = /^[A-Z0-9]{6,35}$/;

export const UPI_NOTE_MAX = 80;
export const UPI_PAYEE_MAX = 50;

export function normalizeUpiId(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

export function isValidUpiId(value) {
  return VPA_RE.test(normalizeUpiId(value));
}

export function normalizeTxnId(value) {
  return String(value ?? '')
    .replace(/[\s-]+/g, '')
    .toUpperCase();
}

export function isValidTxnId(value) {
  return TXN_RE.test(normalizeTxnId(value));
}

export function normalizeAmount(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
}

export function buildUpiPayLink({ upiId, payeeName, amount, note }) {
  const pa = normalizeUpiId(upiId);
  if (!isValidUpiId(pa)) return null;
  const parts = [`pa=${encodeURIComponent(pa)}`];
  const pn = String(payeeName || '')
    .trim()
    .slice(0, UPI_PAYEE_MAX);
  if (pn) parts.push(`pn=${encodeURIComponent(pn)}`);
  const am = normalizeAmount(amount);
  if (am) parts.push(`am=${am.toFixed(2)}`);
  parts.push('cu=INR');
  const tn = String(note || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, UPI_NOTE_MAX);
  if (tn) parts.push(`tn=${encodeURIComponent(tn)}`);
  return `upi://pay?${parts.join('&')}`;
}

export function formatRupees(amount) {
  const n = Number(amount) || 0;
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

/** UPI intents only open on phones; desktop users scan the QR instead. */
export function isLikelyMobileDevice() {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
}

export function formatFeeDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function isFeeOverdue(fee, now = Date.now()) {
  if (!fee?.dueDate || fee.status !== 'pending') return false;
  const due = new Date(fee.dueDate);
  due.setHours(23, 59, 59, 999);
  return due.getTime() < now;
}
